import { OAuthProvider } from '@seapoint/server/enums';
import { Dot } from 'lucide-react';
import { Button } from '../../../../../design-system/button/button';
import { Inline } from '../../../../../design-system/inline/inline';
import { Stack } from '../../../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../../../utils/exhaustive-check';
import { OAuthProviderLogoIcon } from '../../oauth-provider-logo-icon';

type AllowedOAuthProvider = OAuthProvider.GMAIL | OAuthProvider.XERO;

type OAuthProviderConnectionConfig =
  | {
      isConnected: false;
      onConnect: () => void;
    }
  | {
      isConnected: true;
      email: string;
      onDisconnect: () => void;
    };

type OAuthProviderConnectionSettingsContentProps = {
  provider: AllowedOAuthProvider;
  oauthProviderConnectionConfig: OAuthProviderConnectionConfig;
};

export const OAuthProviderConnectionSettingsContent = ({
  oauthProviderConnectionConfig,
  provider,
}: OAuthProviderConnectionSettingsContentProps) => {
  const { isConnected } = oauthProviderConnectionConfig;
  const { title, description } = getOAuthProviderConnectionSettingsContent(provider);

  if (!isConnected) {
    return (
      <Stack space={1} align="stretch">
        <Inline justify="between">
          <Inline space={2}>
            <OAuthProviderLogoIcon provider={provider} className="h-5 w-5" />

            <h3 className="text-lg leading-6 font-medium tracking-tight text-black">{title}</h3>
          </Inline>

          <Button variant="secondary" onClick={oauthProviderConnectionConfig.onConnect}>
            Connect
          </Button>
        </Inline>

        <p className="leading-6 font-medium tracking-tight text-muted-foreground">{description}</p>
      </Stack>
    );
  }

  return (
    <Stack space={1} align="stretch">
      <Inline space={2}>
        <OAuthProviderLogoIcon provider={provider} className="h-5 w-5" />

        <h3 className="text-lg leading-6 font-medium tracking-tight text-black">{title}</h3>
      </Inline>

      <Inline justify="between">
        <Inline space={1} className="rounded-full bg-muted px-2 py-1">
          <div className="h-5 w-5">
            <Dot color="#169B62" className="h-full w-full" />
          </div>

          <p className="text-xs leading-6 font-medium tracking-tight text-black">
            {oauthProviderConnectionConfig.email}
          </p>
        </Inline>

        <Button variant="link" onClick={oauthProviderConnectionConfig.onDisconnect} className="text-destructive">
          Disconnect
        </Button>
      </Inline>
    </Stack>
  );
};

const getOAuthProviderConnectionSettingsContent = (provider: AllowedOAuthProvider) => {
  switch (provider) {
    case OAuthProvider.GMAIL:
      return {
        title: 'Gmail account',
        description: `We'll scan your inbox and automatically pull any invoices received into Seapoint.`,
      };
    case OAuthProvider.XERO:
      return {
        title: 'Xero account',
        description: `We'll send invoices paid through Seapoint directly into Xero.`,
      };
    default:
      return exhaustiveCheck(provider);
  }
};
